import { useState, type FormEvent } from 'react';
import { patchMyTutorProfile } from '@/lib/tutor-api';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { FooterButtons, type StepProps, withCompletedStep } from './shared';

export function RateStep({ profile, accessToken, onSaved, onError }: StepProps) {
  const [usd, setUsd] = useState(
    profile?.hourlyRate.usdMinor ? String(profile.hourlyRate.usdMinor / 100) : '',
  );
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!usd.trim()) {
      onError(new Error('Please enter your hourly rate.'));
      return;
    }
    const usdMinor = Math.round(Number.parseFloat(usd) * 100);
    if (!Number.isFinite(usdMinor)) {
      onError(new Error('Please enter a valid hourly rate.'));
      return;
    }
    if (usdMinor <= 0) {
      onError(new Error('Hourly rate must be greater than zero.'));
      return;
    }
    setBusy(true);
    try {
      const next = await patchMyTutorProfile(accessToken, {
        hourlyRate: { usdMinor },
        completedSteps: withCompletedStep(profile, 'rate'),
      });
      onSaved(next);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="space-y-5" onSubmit={submit}>
      <p className="text-sm text-muted-foreground">
        Set your hourly rate in US dollars. This is what parents will see when browsing your profile.
      </p>

      <div className="space-y-2">
        <Label htmlFor="rate-usd">Hourly rate (USD)</Label>
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">$</span>
          <Input
            id="rate-usd"
            type="number"
            inputMode="decimal"
            step="0.01"
            min={0}
            value={usd}
            onChange={(e) => setUsd(e.target.value)}
            className="pl-7"
            placeholder="e.g. 10"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Currency: USD
        </p>
      </div>

      <FooterButtons primaryLabel="Save & continue" busy={busy} />
    </form>
  );
}
